import {
    type ActorRule,
    type GetDataAccessPoliciesParams,
    type GovernancePolicy,
    type GovernancePolicyEntity,
    type GovernancePolicyEntityPagedResult,
    PolicyStatus,
    Scope,
} from '@experiences/interfaces';
import buildQuery from 'odata-query';

import {
    axiosDelete,
    get,
    patch,
    post,
} from '../utility/Requests';

/**
 * Strip UI-only fields from the actor rule before sending to the API.
 * - Removes `valueNames` from each actor identity value (including Group entries)
 */
function prepareActorRuleForApi(actorRule: ActorRule) {
    return {
        values: actorRule.values.map(({
            valueNames: _vn, ...rest
        }) => rest),
    };
}

/** Sanitize a policy payload for the API by stripping UI-only fields. */
function sanitizePolicyForApi<T extends GovernancePolicy>(policy: T): T {
    if (!policy.actorRule) {
        return policy;
    }

    return {
        ...policy,
        actorRule: prepareActorRuleForApi(policy.actorRule),
    };
}

export async function createDataAccessPolicy({
    accountId, policy,
}: {
    accountId: string;
    policy: GovernancePolicy;
}) {
    return post<GovernancePolicyEntity>('/pdp_/api/policies', {
        body: sanitizePolicyForApi(policy),
        stripPortalPath: true,
        headers: {
            'x-uipath-internal-accountid': accountId,
            'Content-Type': 'application/json',
        },
    });
}

export async function updateDataAccessPolicy({
    accountId, policy,
}: {
    accountId: string;
    policy: GovernancePolicyEntity;
}) {
    return patch(`/pdp_/api/policies`, {
        body: sanitizePolicyForApi(policy),
        stripPortalPath: true,
        headers: {
            'x-uipath-internal-accountid': accountId,
            'Content-Type': 'application/json',
        },
    });
}

export async function getDataAccessPolicies({
    accountId, top = 10, skip = 0, filter, orderBy,
}: GetDataAccessPoliciesParams) {
    // Convert filter to string if it's an object
    let filterString: string | undefined;
    if (filter) {
        if (typeof filter === 'string') {
            filterString = filter;
        } else {
            // Use buildQuery to build the filter, then extract the $filter value
            const odataQuery = buildQuery({ filter });
            const urlParams = new URLSearchParams(odataQuery.substring(1)); // Remove leading '?'
            filterString = urlParams.get('$filter') ?? undefined;
        }
    }

    const response = await get<GovernancePolicyEntityPagedResult>('/pdp_/api/policies', {
        stripPortalPath: true,
        urlParams: {
            top,
            skip,
            filter: filterString,
            orderBy,
        },
        headers: { 'x-uipath-internal-accountid': accountId },
    });

    // Transform the data: filter out deleted policies and add scope field
    const transformedResults = (response.results ?? [])
        .filter((policy) => policy.status !== PolicyStatus.Deleted)
        .map((policy) => ({
            ...policy,
            scope: policy.tenantId && policy.tenantId.trim() !== '' ? Scope.Tenant : Scope.Organization,
        }));

    return {
        ...response,
        results: transformedResults,
    };
}

export async function getDataAccessPolicyById({
    accountId, policyId,
}: {
    accountId: string;
    policyId: string;
}) {
    return get<GovernancePolicyEntity>(`/pdp_/api/policies/${policyId}`, {
        stripPortalPath: true,
        headers: { 'x-uipath-internal-accountid': accountId },
    });
}

export async function deleteDataAccessPolicies({
    accountId, policyIds,
}: {
    accountId: string;
    policyIds: string[];
}) {
    return axiosDelete('/pdp_/api/policies', {
        stripPortalPath: true,
        urlParams: { policyId: policyIds },
        headers: { 'x-uipath-internal-accountid': accountId },
    });
}
